// FeedbackCard.tsx — one row on the public board: vote column + summary.
import styled from 'styled-components';
import { softCardInteraction, softControlMotion } from '@/theme/interactions';
import { Link } from 'react-router-dom';
import { Avatar, IconButton, Popover, Button, useToast } from '@/ui';
import { Icon } from '@/icons';
import type { FeedbackListItem } from '@/core/models/feedback';
import { FEEDBACK_CATEGORY_LABELS, FEEDBACK_PLATFORM_LABELS } from '@/core/models/feedback';
import { CATEGORY_ICONS, displayFeedbackId, relativeTime } from './feedbackMeta';
import { StatusBadge } from './StatusBadge';

const Row = styled.article`
  ${softCardInteraction}
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 24px;
  padding: 24px 18px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surface};
  background-image: linear-gradient(
    120deg,
    ${({ theme }) => theme.color.brandFrom}09,
    transparent 70%
  );
  @media (max-width: 600px) {
    padding: 16px 12px;
    gap: 12px;
  }
`;

/**
 * Upvote control. A button, not a link — it mutates. Pressed state is conveyed
 * with aria-pressed so it is not colour-only.
 */
const Upvote = styled.button<{ $on: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 48px;
  padding: 8px 6px;
  width: 100%;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on }) => ($on ? `${theme.color.brandFrom}22` : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.brandFrom : theme.color.textPrimary)};
  font-family: ${({ theme }) => theme.font.body};

  &:hover {
    background-color: ${({ theme }) => `${theme.color.brandFrom}22`};
  }
  ${softControlMotion}
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/** Caret pointing up — Icon takes no style prop, so rotate via a wrapper. */
const Caret = styled.span`
  display: inline-flex;
  transform: rotate(180deg);
`;

const Count = styled.span`
  font-size: 26px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const Main = styled.div`
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;
  @media (max-width: 600px) {
    display: block;
    > div:last-child {
      justify-content: space-between;
      margin-bottom: 12px;
    }
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`;

const CategoryChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 18px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.color.brandFrom}12;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textMuted};
`;

const TitleLink = styled(Link)`
  display: block;
  margin: 0 0 20px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(16px, 1.5vw, 20px);
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Body = styled.p`
  margin: 0 0 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const PublicId = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Avatars = styled.span`
  display: inline-flex;

  > * + * {
    margin-left: -8px;
  }
`;

const AvatarRing = styled.span`
  display: inline-flex;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 2px solid ${({ theme }) => theme.color.bgRoot};
`;

const HiddenTag = styled.span`
  color: ${({ theme }) => theme.color.danger};
  font-weight: 700;
`;

const PinnedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.color.brandFrom};
  font-weight: 700;
`;

const VoteStack = styled.div`
  align-self: start;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.brandFrom}0c;
  padding: 6px 0;
`;
const RemoveVote = styled.button`
  display: grid;
  place-items: center;
  border: 0;
  background: none;
  width: 100%;
  min-height: 36px;
  color: ${({ theme }) => theme.color.brandFrom};
  cursor: pointer;
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const DetailLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  padding: 9px 14px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.brandFrom}0d;
  text-decoration: none;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
  @media (max-width: 600px) {
    margin-left: 0;
  }
`;

export interface FeedbackCardProps {
  item: FeedbackListItem;
  onUpvote: (id: string) => void;
}

export function FeedbackCard({ item, onUpvote }: FeedbackCardProps) {
  // Up to 3 distinct recent public-commenter avatars — served directly by the
  // list endpoint (FeedbackItemListSerializer.get_participants), viewer-
  // independent. Defensive fallback for any older/mocked payload that omits it.
  const participants = item.participants ?? [];
  const toast = useToast();
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/feedback/${item.id}`);
      toast('Feedback link copied');
    } catch {
      toast('Could not copy the link. Open the feedback and copy its address.');
    }
  }
  return (
    <Row data-testid={`feedback-card-${item.id}`}>
      <VoteStack>
        <Upvote
          type="button"
          $on={item.upvoted}
          aria-pressed={item.upvoted}
          aria-label={`Upvote "${item.title}" (${item.upvotes})`}
          onClick={() => onUpvote(item.id)}
        >
          <Caret>
            <Icon name="chevron-down" size={14} />
          </Caret>
          <Count>{item.upvotes}</Count>
        </Upvote>
        <RemoveVote
          aria-label={`Remove your vote from "${item.title}"`}
          disabled={!item.upvoted}
          onClick={() => onUpvote(item.id)}
        >
          <Icon name="chevron-down" size={22} />
        </RemoveVote>
      </VoteStack>

      <Main>
        <TopRow>
          <TitleBlock>
            <CategoryChip>
              <Icon name={CATEGORY_ICONS[item.category]} size={12} />
              {FEEDBACK_CATEGORY_LABELS[item.category]}
            </CategoryChip>
            <TitleLink to={`/feedback/${item.id}`}>
              {item.pinned && (
                <PinnedTag>
                  <Icon name="pin" size={12} />
                </PinnedTag>
              )}
              {item.title}
            </TitleLink>
          </TitleBlock>
          <Actions>
            <StatusBadge status={item.status} />
            <Popover
              trigger={
                <IconButton icon="more-horizontal" label={`More options for ${item.title}`} />
              }
            >
              <Button variant="ghost" onClick={() => void copyLink()}>
                <Icon name="link" size={14} /> Copy link
              </Button>
            </Popover>
          </Actions>
        </TopRow>

        <Body>{item.body}</Body>

        <Meta>
          <PublicId>{displayFeedbackId(item.id, item.createdAt)}</PublicId>
          <Chip>
            <Avatar name={item.author.name} size={18} />
            {item.author.handle}
          </Chip>
          {item.platform !== 'unspecified' && (
            <Chip>{FEEDBACK_PLATFORM_LABELS[item.platform]}</Chip>
          )}
          <span>{relativeTime(item.createdAt)}</span>
          <Chip>
            <Icon name="comment" size={12} />
            {item.commentCount}
          </Chip>
          {participants.length > 0 && (
            <Avatars aria-hidden="true">
              {participants.slice(0, 3).map((p, i) => (
                <AvatarRing key={i}>
                  <Avatar name={p.name} avatarKey={p.av} size={18} />
                </AvatarRing>
              ))}
            </Avatars>
          )}
          {item.removed && <HiddenTag>Hidden</HiddenTag>}
          <DetailLink to={`/feedback/${item.id}`}>
            View details <Icon name="arrow-right" size={16} />
          </DetailLink>
        </Meta>
      </Main>
    </Row>
  );
}
